// src/pages/AssetCategories.tsx
import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, AlertCircle, Edit2, Check, X, FolderOpen } from 'lucide-react';
import { assetsAPI } from '../services/assetsAPI';
import { AssetCategory, CreateAssetCategoryRequest } from '../types/asset';
import { logger } from '../utils/logger';

const pageLogger = logger.createServiceLogger('AssetCategoriesPage');

const AssetCategories: React.FC = () => {
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [newCategory, setNewCategory] = useState({ name: '', description: '', verificationFrequencyMonths: '12', requiresPhotoOnVerification: false });
  const [editData, setEditData] = useState({ name: '', description: '', verificationFrequencyMonths: '12', requiresPhotoOnVerification: false, isActive: true });

  useEffect(() => { loadCategories(); }, []);

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await assetsAPI.getCategories();
      if (response.success && response.data) {
        setCategories(response.data.categories || []);
      } else {
        throw new Error(response.error?.message || 'Failed to load categories');
      }
    } catch (err: any) {
      pageLogger.error('Error loading categories', { error: err.message });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategory.name.trim()) return;
    try {
      const response = await assetsAPI.createCategory({
        name: newCategory.name,
        description: newCategory.description || undefined,
        verificationFrequencyMonths: parseInt(newCategory.verificationFrequencyMonths) || 12,
        requiresPhotoOnVerification: newCategory.requiresPhotoOnVerification
      });
      if (response.success) {
        setNewCategory({ name: '', description: '', verificationFrequencyMonths: '12', requiresPhotoOnVerification: false });
        setShowAddForm(false);
        loadCategories();
      } else {
        throw new Error(response.error?.message || 'Failed to create category');
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const startEdit = (cat: AssetCategory) => {
    setEditingId(cat.categoryId);
    setEditData({
      name: cat.name,
      description: cat.description || '',
      verificationFrequencyMonths: cat.verificationFrequencyMonths.toString(),
      requiresPhotoOnVerification: cat.requiresPhotoOnVerification,
      isActive: cat.isActive
    });
  };

  const handleUpdateCategory = async (categoryId: string) => {
    try {
      const response = await assetsAPI.updateCategory(categoryId, {
        name: editData.name,
        description: editData.description || undefined,
        verificationFrequencyMonths: parseInt(editData.verificationFrequencyMonths) || 12,
        requiresPhotoOnVerification: editData.requiresPhotoOnVerification,
        isActive: editData.isActive
      });
      if (response.success) {
        setEditingId(null);
        loadCategories();
      } else {
        throw new Error(response.error?.message || 'Failed to update category');
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleInitializeDefaults = async () => {
   if (!window.confirm('This will add default asset categories. Continue?')) return;
    try {
      const response = await assetsAPI.initializeDefaultCategories();
      if (response.success) {
        loadCategories();
      } else {
        throw new Error(response.error?.message || 'Failed to initialize categories');
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading categories...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asset Categories</h1>
          <p className="text-sm text-gray-500 mt-1">Manage categories for tracked assets</p>
        </div>
        <div className="flex space-x-2">
          {categories.length === 0 && (
            <button onClick={handleInitializeDefaults} className="px-4 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg">
              Load Defaults
            </button>
          )}
          <button onClick={() => setShowAddForm(true)} className="inline-flex items-center px-4 py-2 text-white rounded-lg" style={{ backgroundColor: '#082759' }}>
            <Plus className="w-5 h-5 mr-2" />Add Category
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Category</h3>
          <form onSubmit={handleAddCategory} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" value={newCategory.name} onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., IT Equipment" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Verification Frequency (months)</label>
                <input type="number" value={newCategory.verificationFrequencyMonths} onChange={(e) => setNewCategory(prev => ({ ...prev, verificationFrequencyMonths: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" min="1" max="60" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input type="text" value={newCategory.description} onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Optional description" />
            </div>
            <div className="flex items-center">
              <input type="checkbox" id="newRequiresPhoto" checked={newCategory.requiresPhotoOnVerification} onChange={(e) => setNewCategory(prev => ({ ...prev, requiresPhotoOnVerification: e.target.checked }))} className="rounded" />
              <label htmlFor="newRequiresPhoto" className="ml-2 text-sm text-gray-700">Require photo on verification</label>
            </div>
            <div className="flex justify-end space-x-2">
              <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 text-white rounded-lg" style={{ backgroundColor: '#082759' }}>Add Category</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verification</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No categories</p>
                  <p className="text-sm mt-1">Click "Load Defaults" or add categories manually</p>
                </td>
              </tr>
            ) : (
              categories.map((cat) => (
                <tr key={cat.categoryId} className={!cat.isActive ? 'opacity-50' : ''}>
                  {editingId === cat.categoryId ? (
                    <>
                      <td className="px-6 py-4">
                        <input type="text" value={editData.name} onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))} className="w-full px-2 py-1 border border-gray-300 rounded" />
                        <input type="text" value={editData.description} onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-2 py-1 border border-gray-300 rounded mt-1" placeholder="Description" />
                      </td>
                      <td className="px-6 py-4">
                        <input type="number" value={editData.verificationFrequencyMonths} onChange={(e) => setEditData(prev => ({ ...prev, verificationFrequencyMonths: e.target.value }))} className="w-20 px-2 py-1 border border-gray-300 rounded" min="1" max="60" /> months
                      </td>
                      <td className="px-6 py-4">
                        <select value={editData.isActive ? 'active' : 'inactive'} onChange={(e) => setEditData(prev => ({ ...prev, isActive: e.target.value === 'active' }))} className="px-2 py-1 border border-gray-300 rounded">
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => handleUpdateCategory(cat.categoryId)} className="p-1 text-green-600 hover:bg-green-50 rounded mr-1"><Check className="w-5 h-5" /></button>
                        <button onClick={() => setEditingId(null)} className="p-1 text-gray-600 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{cat.name}</div>
                        {cat.description && <div className="text-sm text-gray-500">{cat.description}</div>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        Every {cat.verificationFrequencyMonths} months
                        {cat.requiresPhotoOnVerification && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Photo required</span>}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${cat.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {cat.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => startEdit(cat)} className="p-1 text-gray-600 hover:bg-gray-100 rounded"><Edit2 className="w-5 h-5" /></button>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AssetCategories;